import { motion } from "framer-motion";
import { Phone, ArrowRight, ShieldCheck, Star, PoundSterling, Building2, Instagram, Facebook } from "lucide-react";
import { CONTACT } from "@/data";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

const HERO_IMAGE =
  "https://images.unsplash.com/flagged/photo-1564767609213-c75ee685263a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBsb2Nrc21pdGglMjBmaXhpbmclMjBkb29yfGVufDB8fHx8MTc4MTc4MzUwMXww&ixlib=rb-4.1.0&q=85&w=1920";

const trust = [
  { icon: ShieldCheck, title: "Fully Insured", sub: "Public liability covered" },
  { icon: Star, title: "5-Star Rated", sub: "By local customers" },
  { icon: PoundSterling, title: "Free Quotations", sub: "No-obligation visits" },
  { icon: Building2, title: "Residential & Commercial", sub: "Homes, offices, sites" },
];

export const Hero = () => {
  return (
    <section id="hero" data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Full-bleed background */}
      <div className="absolute inset-0">
        <img src={HERO_IMAGE} alt="Professional MJL locksmith fitting a door lock in Buckinghamshire" className="w-full h-full object-cover animate-kenburns" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#161619]/90 via-[#1c1c20]/55 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#161619]/95 via-transparent to-transparent" />
        <div className="absolute -top-28 -left-28 w-[40rem] h-[40rem] bg-[#E63946]/15 blur-[170px] rounded-full" />
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-44 lg:pb-56">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 backdrop-blur px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-gray-200">
            <span className="w-2 h-2 rounded-full bg-[#22C55E] animate-pulse" />
            Serving {CONTACT.area}
          </span>

          <h1 className="font-heading mt-6 text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[0.98]">
            <span className="text-steel">Professional Locksmith &</span>{" "}
            <span className="text-gradient-red">Property Maintenance</span>{" "}
            <span className="text-steel">in Buckinghamshire.</span>
          </h1>

          <p className="mt-6 text-base md:text-lg text-gray-300 leading-relaxed max-w-2xl">
            From emergency lock replacements and security upgrades to repairs, bespoke joinery
            and full house &amp; rubbish clearances — reliable, affordable solutions for homes
            and businesses.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <button
              data-testid="hero-quote-button"
              onClick={() => scrollTo("contact")}
              className="btn-press inline-flex items-center justify-center gap-2 rounded-full bg-[#E63946] hover:bg-[#D62828] text-white px-7 py-4 text-base font-bold shadow-[0_0_30px_rgba(230,57,70,0.3)]"
            >
              Get a Free Quote <ArrowRight className="w-5 h-5" />
            </button>
            <a
              data-testid="hero-call-button"
              href={`tel:${CONTACT.phoneRaw}`}
              className="btn-press inline-flex items-center justify-center gap-2 rounded-full glass hover:bg-white/10 text-white px-7 py-4 text-base font-bold"
            >
              <Phone className="w-5 h-5 text-[#E63946]" /> Call Now: {CONTACT.phone}
            </a>
          </div>

          <div className="mt-6 flex items-center gap-3">
            <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">Follow us</span>
            <a
              data-testid="hero-instagram"
              href={CONTACT.instagram}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="MJL on Instagram"
              className="flex items-center justify-center w-10 h-10 rounded-full glass hover:bg-[#E63946] hover:border-[#E63946] text-white transition-colors"
            >
              <Instagram className="w-5 h-5" />
            </a>
            <a
              data-testid="hero-facebook"
              href={CONTACT.facebook}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="MJL on Facebook"
              className="flex items-center justify-center w-10 h-10 rounded-full glass hover:bg-[#E63946] hover:border-[#E63946] text-white transition-colors"
            >
              <Facebook className="w-5 h-5" />
            </a>
          </div>
        </motion.div>
      </div>

      {/* Floating trust bar overlapping hero base */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.2 }}
        data-testid="hero-trust-bar"
        className="absolute z-20 inset-x-4 sm:inset-x-6 lg:inset-x-0 bottom-6 lg:bottom-0 lg:translate-y-1/2 max-w-7xl lg:mx-auto lg:left-0 lg:right-0 lg:px-8"
      >
        <div className="glass elev rounded-2xl grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-white/10">
          {trust.map((t) => (
            <div key={t.title} data-testid={`trust-bar-${t.title}`} className="flex items-center gap-3 p-4 sm:p-5">
              <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-[#E63946]/15 text-[#E63946] shrink-0 ring-1 ring-[#E63946]/20">
                <t.icon className="w-5 h-5" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-bold text-white leading-tight">{t.title}</p>
                <p className="text-xs text-gray-400 mt-0.5 truncate">{t.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};
